import React from 'react';
import clsx from 'clsx';
import { createCategoryPrintJob, syncJudgingCategoryCounts } from '../../services/rpc';
import type { CategorySearchFilters } from '../../types/rpc-contracts';
import type { VettingStatus } from '../../types/vetting';
import FeatureIntro from '../../components/common/FeatureIntro';
import PaginationControls from '../../components/common/PaginationControls';
import CategoryFilterPanel from '../../components/CategoryFilterPanel';
import { useCategoriesQualityController } from './use-categories-quality-controller';
import styles from './CategoriesQualityView.module.css';

const VETTING_STATUS_ORDER: VettingStatus[] = ['green', 'yellow', 'red'];
const TABLE_COLUMN_COUNT = 5;

const vettingLabels: Record<VettingStatus, string> = {
  green: 'Approved',
  yellow: 'Review',
  red: 'Not suitable',
};

interface CategoriesQualityViewProps {
  className?: string;
}

const CategoriesQualityView: React.FC<CategoriesQualityViewProps> = ({ className = '' }) => {
  const {
    searchInput,
    setSearchInput,
    committedSearch,
    applySearch,
    clearSearch,
    hasBisac,
    setHasBisac,
    hasDisplayName,
    setHasDisplayName,
    hasBucketLevel1,
    setHasBucketLevel1,
    bucketLevel2,
    setBucketLevel2,
    vettingStatuses,
    setVettingStatuses,
    sortBy,
    sortDirection,
    requestSort,
    limit,
    setLimit,
    page,
    setPage,
    selectedIds,
    setSelectedIds,
    searchFilters,
    query,
  } = useCategoriesQualityController();
  const [syncingJudgingCounts, setSyncingJudgingCounts] = React.useState(false);

  const { data, isLoading, isError, error, refetch } = query;

  const rows = data?.rows ?? [];
  const total = data?.total_count ?? 0;

  const handleSyncJudgingCounts = React.useCallback(async () => {
    if (syncingJudgingCounts) {
      return;
    }
    setSyncingJudgingCounts(true);
    try {
      const result = await syncJudgingCategoryCounts();
      await refetch();
      alert(`Judging category counts synchronized for ${result.updated_rows} program${result.updated_rows === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Failed to sync judging category counts', error);
      alert('Failed to synchronize judging category counts. Please try again.');
    } finally {
      setSyncingJudgingCounts(false);
    }
  }, [refetch, syncingJudgingCounts]);

  const highlightText = (text?: string | null): React.ReactNode => {
    const q = (committedSearch || '').trim();
    if (!text) return text ?? '';
    if (!q) return text;
    const terms = Array.from(new Set(q.split(/\s+/).filter(t => t.length >= 2)));
    if (terms.length === 0) return text;
    const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
    const parts = text.split(regex);
    return (
      <span>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark key={i} className={styles.highlight}>{part}</mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  };

  const resetPagination = React.useCallback(() => {
    setPage(0);
  }, [setPage]);

  const handleSearchSubmit = () => {
    applySearch();
  };

  const handleClearSearch = () => {
    clearSearch();
  };

  const handleToggleSelect = (categoryId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(Math.max(nextPage, 0));
  };

  const handleItemsPerPageChange = (items: number) => {
    setLimit(items);
  };

  const handleCreatePrintJob = async (selectedOnly: boolean) => {
    try {
      const { limit: _limit, offset: _offset, ...printFilters } = searchFilters;
      const jobId = await createCategoryPrintJob({
        filters: printFilters as CategorySearchFilters,
        selectedIds: selectedOnly ? Array.from(selectedIds) : undefined
      });
      const url = `${window.location.origin}/#print-cat-results?job_id=${encodeURIComponent(jobId)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to create category print job', err);
      alert('Failed to prepare print results. Please try again.');
    }
  };

  const allSelected = rows.length > 0 && rows.every(row => selectedIds.has(row.category_id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        rows.forEach(row => next.add(row.category_id));
        return next;
      });
    }
  };

  const sortIndicator = (key: NonNullable<CategorySearchFilters['sort_by']>) => {
    if (sortBy !== key) return '↕';
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  const handleExportCsv = () => {
    if (!rows.length) return;
    const header = ['Program', 'BACI Code', 'Category', 'Display Name', 'Audience', 'Content Type', 'Description'];
    const csvRows = rows.map(row => [
      row.program_name || '',
      row.program_code || '',
      row.category_original_name || row.category_name || '',
      row.category_display_name || '',
      row.bucket_level_1 || '',
      row.bucket_level_2 || '',
      row.category_description || ''
    ]);
    const escapeCsv = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const body = [header, ...csvRows].map(row => row.map(cell => escapeCsv(String(cell ?? ''))).join(',')).join('\n');
    const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'categories.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintResults = async () => {
    await handleCreatePrintJob(selectedIds.size > 0);
  };

  const clearBisac = () => { setHasBisac(undefined); resetPagination(); };
  const currentPage = page;

  const chips: Array<React.ReactNode> = [];
  if (committedSearch) {
    chips.push(
      <span key="search" className={clsx(styles.chip, styles.chipSearch)}>
        Search: “{committedSearch}”
        <button
          type="button"
          onClick={handleClearSearch}
          className={styles.chipDismiss}
        >
          ✕
        </button>
      </span>
    );
  }

  if (hasBisac !== undefined) {
    const positive = hasBisac === true;
    chips.push(
      <span
        key="bisac"
        className={clsx(
          styles.chip,
          positive ? styles.chipBisacPositive : styles.chipBisacNegative
        )}
      >
        BISAC: {positive ? 'Has mapping' : 'No mapping'}
        <button type="button" onClick={clearBisac} className={styles.chipDismiss}>✕</button>
      </span>
    );
  }

  if (bucketLevel2.length > 0) {
    chips.push(
      <span
        key="bucket2"
        className={clsx(styles.chip, styles.chipBucket2)}
      >
        Buckets: {bucketLevel2.join(', ')}
        <button
          type="button"
          onClick={() => {
            setBucketLevel2([]);
            resetPagination();
          }}
          className={styles.chipDismiss}
        >
          ✕
        </button>
      </span>
    );
  }

  if (vettingStatuses.length && vettingStatuses.length < VETTING_STATUS_ORDER.length) {
    const labels = vettingStatuses.map(status => vettingLabels[status]);
    chips.push(
      <span
        key="vetting-statuses"
        className={clsx(styles.chip, styles.chipVetting)}
      >
        Vetting: {labels.join(', ')}
        <button
          type="button"
          onClick={() => {
            setVettingStatuses(VETTING_STATUS_ORDER);
            resetPagination();
          }}
          className={styles.chipDismiss}
          title="Clear vetting filter"
        >
          ✕
        </button>
      </span>
    );
  }

  if (hasDisplayName === false) {
    chips.push(
      <span
        key="display-name-missing"
        className={clsx(styles.chip, styles.chipAttention)}
      >
        Missing display name
        <button
          type="button"
          onClick={() => setHasDisplayName(undefined)}
          className={styles.chipDismiss}
        >
          ✕
        </button>
      </span>
    );
  }

  if (hasBucketLevel1 === false) {
    chips.push(
      <span
        key="bucket-missing"
        className={clsx(styles.chip, styles.chipAttention)}
      >
        No bucket assigned
        <button
          type="button"
          onClick={() => setHasBucketLevel1(undefined)}
          className={styles.chipDismiss}
        >
          ✕
        </button>
      </span>
    );
  }

  return (
    <div className={clsx('categories-quality-hub', styles.container, className)}>
      <FeatureIntro eyebrow="Award Categories View" />

      <CategoryFilterPanel
        filters={{
          has_bisac: hasBisac,
          vetting_statuses: vettingStatuses.length === VETTING_STATUS_ORDER.length ? undefined : vettingStatuses,
          has_display_name: hasDisplayName,
          has_bucket_level_1: hasBucketLevel1,
          bucket_level_2: bucketLevel2,
        }}
        onFiltersChange={(newFilters) => {
          if (newFilters.has_bisac !== hasBisac) {
            setHasBisac(newFilters.has_bisac);
            resetPagination();
          }
          if (newFilters.vetting_statuses !== vettingStatuses) {
            setVettingStatuses(newFilters.vetting_statuses ?? VETTING_STATUS_ORDER);
            resetPagination();
          }
          if (newFilters.has_display_name !== hasDisplayName) {
            setHasDisplayName(newFilters.has_display_name);
            resetPagination();
          }
          if (newFilters.has_bucket_level_1 !== hasBucketLevel1) {
            setHasBucketLevel1(newFilters.has_bucket_level_1);
            resetPagination();
          }
          if (newFilters.bucket_level_2 !== bucketLevel2) {
            setBucketLevel2(newFilters.bucket_level_2 ?? []);
            resetPagination();
          }
        }}
        categoryCount={total}
        isLoading={isLoading}
        onSyncJudgingCounts={handleSyncJudgingCounts}
        syncingJudgingCounts={syncingJudgingCounts}
      />

      <div className={styles.searchCard}>
        <div className={styles.searchRow}>
          <div className={styles.searchInputs}>
            <input
              type="text"
              placeholder="Search categories..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { handleSearchSubmit(); } }}
              className={styles.searchInput}
            />
            <button
              type="button"
              onClick={handleSearchSubmit}
              className={styles.searchButton}
            >
              Search
            </button>
          </div>
        </div>

      <div className={styles.filterTags}>
        {chips.length > 0 && chips}
      </div>
    </div>

    <div className={styles.resultsHeader}>
      <div className={styles.resultsSummary}>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isLoading}
          className={styles.resultsButton}
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
        <button
          type="button"
          onClick={handlePrintResults}
          className={styles.resultsButton}
          disabled={!rows.length}
        >
          Print results
        </button>
        <button
          type="button"
          onClick={handleExportCsv}
          className={styles.resultsButton}
          disabled={!rows.length}
          title="Export current results to CSV"
        >
          Export CSV
        </button>
      </div>
      {total > 0 && (
        <PaginationControls
          currentPage={currentPage}
          pageSize={limit}
          totalCount={total}
          currentCount={rows.length}
          onPageChange={handlePageChange}
          onPageSizeChange={handleItemsPerPageChange}
          pageSizeOptions={[25, 50, 100]}
          showSummaryText={false}
        />
      )}
    </div>
    <div className={styles.tableCard}>
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr>
                <th className={clsx(styles.headerCell, styles.checkboxColumn)}>
                  <div className={styles.checkboxActions}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all categories on page"
                    />
                    <span className={styles.checkboxSpacer} aria-hidden="true" />
                  </div>
                </th>
                <th className={clsx(styles.headerCell, styles.baciColumn, styles.centerCell)}>BACI</th>
                <th className={clsx(styles.headerCell, styles.sortable)} onClick={() => requestSort('display_name')}>
                  Display Name {sortIndicator('display_name')}
                </th>
                <th className={clsx(styles.headerCell, styles.attributeColumn)}>Category Group</th>
                <th className={clsx(styles.headerCell, styles.descriptionColumn)}>Description</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={TABLE_COLUMN_COUNT} className={clsx(styles.cell, styles.rowMessage)}>
                    Loading categories…
                  </td>
                </tr>
              )}
              {isError && !isLoading && (
                <tr>
                  <td colSpan={TABLE_COLUMN_COUNT} className={clsx(styles.cell, styles.rowMessage, styles.rowMessageError)}>
                    {(error as Error)?.message ?? 'Failed to load categories'}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && rows.length === 0 && (
                <tr>
                  <td colSpan={TABLE_COLUMN_COUNT} className={clsx(styles.cell, styles.rowMessage, styles.rowMessageEmpty)}>
                    No categories found.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && rows.map((row) => {
                const isSelected = selectedIds.has(row.category_id);

                return (
                  <tr key={row.category_id} className={clsx(styles.tableRow, isSelected && styles.selectedRow)}>
                    <td className={clsx(styles.cell, styles.centerCell, styles.checkboxColumn)}>
                      <div className={styles.checkboxActions}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(row.category_id)}
                          aria-label={`Select ${row.category_original_name || row.category_name || 'category'}`}
                        />
                      </div>
                    </td>
                    <td className={clsx(styles.cell, styles.baciCell)} title={row.program_name}>
                      {highlightText(row.program_code?.slice(0, 3).toUpperCase() || '—')}
                    </td>
                    <td className={styles.cell}>
                      <div className={styles.categoryTextGroup}>
                        <span className={styles.programNameInCell}>{row.program_name}</span>
                        {row.category_display_name ? (
                          <span className={styles.displayName}>{row.category_display_name}</span>
                        ) : (
                          <span className={styles.displayNamePlaceholder}>{row.category_original_name || row.category_name}</span>
                        )}
                      </div>
                    </td>
                    <td className={clsx(styles.cell, styles.attributeCell)}>
                      <div className={styles.bucketTextGroup}>
                        {row.bucket_level_1 && (
                          <span className={styles.bucketLevel1Text}>{row.bucket_level_1}</span>
                        )}
                        {row.bucket_level_2 && (
                          <span className={styles.bucketLevel2Text}>{row.bucket_level_2}</span>
                        )}
                        {!row.bucket_level_1 && !row.bucket_level_2 && (
                          <span className={styles.bucketEmptyText}>—</span>
                        )}
                        {row.is_junk && (
                          <span className={clsx(styles.attributeChip, styles.attributeChipWarning)}>Junk</span>
                        )}
                      </div>
                    </td>
                    <td className={clsx(styles.cell, styles.descriptionCell)}>
                      <span className={styles.descriptionText} title={row.category_description || undefined}>
                        {row.category_description || '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {total > 0 && (
        <div className={styles.paginationWrapper}>
          <PaginationControls
            currentPage={currentPage}
            pageSize={limit}
            totalCount={total}
            currentCount={rows.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handleItemsPerPageChange}
            pageSizeOptions={[25, 50, 100]}
          />
        </div>
      )}
    </div>
  );
};

export default CategoriesQualityView;
